/**
 * Pipeline Status Tracker Component
 * Real-time visualization of pipeline execution progress
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { adminFetchHeaders } from '@/hooks/useAdminFetch';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Play,
  RefreshCw,
  X,
  ChevronDown,
  ChevronUp,
  Globe,
  Brain,
  Database,
  Sparkles
} from 'lucide-react';

const API = (window.location.origin || '') + '/api';

const getAuthHeaders = () => adminFetchHeaders(false);

// Step icons mapping
const STEP_ICONS = {
  scrape: Globe,
  extract_akus: Brain,
  generate_rag: Database,
  generate_questions: Sparkles
};

// Step labels in German
const STEP_LABELS = {
  scrape: 'Web-Scraping',
  extract_akus: 'AKU-Extraktion',
  generate_rag: 'RAG-Generierung',
  generate_questions: 'Fragen-Generierung'
};

// Status colors
const STATUS_COLORS = {
  pending: 'bg-slate-100 text-slate-600',
  running: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-yellow-100 text-yellow-700'
};

const STATUS_LABELS = {
  pending: 'Ausstehend',
  running: 'Läuft',
  completed: 'Abgeschlossen',
  failed: 'Fehlgeschlagen',
  cancelled: 'Abgebrochen'
};

/**
 * Single Pipeline Step Component
 */
const PipelineStep = ({ step, isActive }) => {
  const Icon = STEP_ICONS[step.name] || Clock;
  const label = STEP_LABELS[step.name] || step.name;
  
  const getStatusIcon = () => {
    switch (step.status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'running':
        return <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-slate-400" />;
    }
  };

  return (
    <div 
      className={`
        p-4 rounded-lg border-2 transition-all duration-300
        ${isActive ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-slate-200 bg-white'}
        ${step.status === 'completed' ? 'border-green-300 bg-green-50' : ''}
        ${step.status === 'failed' ? 'border-red-300 bg-red-50' : ''}
      `}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className={`
            p-2 rounded-lg
            ${step.status === 'running' ? 'bg-blue-100' : ''}
            ${step.status === 'completed' ? 'bg-green-100' : ''}
            ${step.status === 'failed' ? 'bg-red-100' : ''}
            ${step.status === 'pending' ? 'bg-slate-100' : ''}
          `}>
            <Icon className={`w-5 h-5 
              ${step.status === 'running' ? 'text-blue-600' : ''}
              ${step.status === 'completed' ? 'text-green-600' : ''}
              ${step.status === 'failed' ? 'text-red-600' : ''}
              ${step.status === 'pending' ? 'text-slate-400' : ''}
            `} />
          </div>
          <div>
            <h4 className="font-medium text-slate-900">{label}</h4>
            {step.current_item && step.status === 'running' && (
              <p className="text-sm text-slate-600">{step.current_item}</p>
            )}
          </div>
        </div>
        {getStatusIcon()}
      </div>
      
      {step.status === 'running' && step.total_items > 0 && (
        <div className="mt-3">
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>{step.processed_items || 0} von {step.total_items}</span>
            <span>{step.progress || 0}%</span>
          </div>
          <Progress value={step.progress || 0} className="h-2" />
        </div>
      )}
      
      {step.error && (
        <div className="mt-2 p-2 bg-red-100 rounded text-sm text-red-700">
          {step.error}
        </div>
      )}
      
      {step.result && step.status === 'completed' && (
        <div className="mt-2 text-xs text-slate-500">
          {step.result.pages_scraped && `${step.result.pages_scraped} Seiten gescrapt`}
          {step.result.akus_extracted && `${step.result.akus_extracted} AKUs extrahiert`}
          {step.result.chunks_created && `${step.result.chunks_created} RAG-Chunks erstellt`}
        </div>
      )}
    </div>
  );
};

/**
 * Single Pipeline Job Card
 */
const PipelineJobCard = ({ job, onRefresh, expanded, onToggle }) => {
  const isActive = job.status === 'running' || job.status === 'pending';
  
  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };
  
  const getDuration = () => {
    if (!job.started_at) return null;
    const start = new Date(job.started_at);
    const end = job.completed_at ? new Date(job.completed_at) : new Date();
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor((diffMs % 60000) / 1000);
    return `${diffMins}:${diffSecs.toString().padStart(2, '0')}`;
  };

  return (
    <Card className={`
      transition-all duration-300
      ${isActive ? 'ring-2 ring-blue-500 shadow-lg' : ''}
    `}>
      <CardHeader 
        className="cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isActive && (
              <div className="relative">
                <div className="absolute inset-0 bg-blue-500 rounded-full animate-ping opacity-25"></div>
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin relative" />
              </div>
            )}
            {job.status === 'completed' && (
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            )}
            {job.status === 'failed' && (
              <XCircle className="w-6 h-6 text-red-600" />
            )}
            {job.status === 'cancelled' && (
              <X className="w-6 h-6 text-yellow-600" />
            )}
            
            <div>
              <CardTitle className="text-lg">
                {job.metadata?.certification_name || job.certification_key}
              </CardTitle>
              <CardDescription className="flex items-center gap-2 mt-1">
                <Badge className={STATUS_COLORS[job.status]}>
                  {STATUS_LABELS[job.status]}
                </Badge>
                <span className="text-xs">
                  Job: {job.job_id}
                </span>
              </CardDescription>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {isActive && (
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">
                  {job.progress || 0}%
                </div>
                <div className="text-xs text-slate-500">
                  Fortschritt
                </div>
              </div>
            )}
            
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent>
          {/* Timeline */}
          <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
            <div>
              <span className="text-slate-400">Gestartet:</span>{' '}
              {formatTime(job.started_at)}
            </div>
            {job.completed_at && (
              <div>
                <span className="text-slate-400">Beendet:</span>{' '}
                {formatTime(job.completed_at)}
              </div>
            )}
            {getDuration() && (
              <div>
                <span className="text-slate-400">Dauer:</span>{' '}
                {getDuration()}
              </div>
            )}
          </div>
          
          {/* Overall Progress */}
          {isActive && (
            <div className="mb-6">
              <Progress value={job.progress || 0} className="h-3" />
            </div>
          )}
          
          {/* Steps */}
          <div className="space-y-3">
            {Array.isArray(job.steps) && job.steps.map((step, idx) => (
              <PipelineStep 
                key={step.name || idx}
                step={step}
                isActive={job.current_step === step.name}
              />
            ))}
          </div>
          
          {/* Error message */}
          {job.error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-700">Fehler:</p>
              <p className="text-sm text-red-600">{job.error}</p>
            </div>
          )}
          
          {/* Summary */}
          {job.summary && job.status === 'completed' && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm font-medium text-green-700">Zusammenfassung:</p>
              <ul className="text-sm text-green-600 mt-1 space-y-1">
                {job.summary.scrape_pages && (
                  <li>• {job.summary.scrape_pages} Seiten gescrapt</li>
                )}
                {job.summary.akus_extracted && (
                  <li>• {job.summary.akus_extracted} AKUs extrahiert</li>
                )}
              </ul>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex gap-2 mt-4">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onRefresh();
              }}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Aktualisieren
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

/**
 * Main Pipeline Status Tracker Component
 */
const PipelineStatusTracker = ({ 
  certificationKey = null,
  showActiveOnly = false,
  autoRefresh = true,
  refreshInterval = 3000,
  maxJobs = 5
}) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedJobId, setExpandedJobId] = useState(null);
  
  const fetchJobs = useCallback(async () => {
    try {
      let url = `${API}/question-engine/pipeline-jobs?limit=${maxJobs}`;
      
      if (certificationKey) {
        url += `&certification_key=${certificationKey}`;
      }
      
      if (showActiveOnly) {
        url += `&active_only=true`;
      }
      
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Fehler beim Laden der Pipeline-Jobs');
      }
      
      const data = await response.json();
      setJobs(data.jobs || []);
      
      // Auto-expand active jobs
      const activeJob = (data.jobs || []).find(j => 
        j.status === 'running' || j.status === 'pending'
      );
      if (activeJob && !expandedJobId) {
        setExpandedJobId(activeJob.job_id);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching pipeline jobs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [certificationKey, showActiveOnly, maxJobs, expandedJobId]);
  
  // Initial fetch
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);
  
  // Auto-refresh for active jobs
  useEffect(() => {
    if (!autoRefresh) return;
    
    const hasActiveJobs = jobs.some(j => 
      j.status === 'running' || j.status === 'pending'
    );
    
    if (!hasActiveJobs) return;
    
    const interval = setInterval(fetchJobs, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, jobs, fetchJobs]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        <span className="ml-3 text-slate-600">Lade Pipeline-Status...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600">{error}</p>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={fetchJobs}
          className="mt-2"
        >
          Erneut versuchen
        </Button>
      </div>
    );
  }
  
  if (jobs.length === 0) {
    return (
      <div className="text-center p-8 bg-slate-50 rounded-lg border border-slate-200">
        <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500">
          {showActiveOnly 
            ? 'Keine aktiven Pipelines' 
            : 'Keine Pipeline-Jobs gefunden'}
        </p>
        <p className="text-sm text-slate-400 mt-1">
          Starten Sie eine Pipeline über die Web-Scraping-Funktion
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">
          Pipeline-Status
          {jobs.filter(j => j.status === 'running').length > 0 && (
            <Badge className="ml-2 bg-blue-100 text-blue-700">
              {jobs.filter(j => j.status === 'running').length} aktiv
            </Badge>
          )}
        </h3>
        <Button
          size="sm"
          variant="ghost"
          onClick={fetchJobs}
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Job Cards */}
      <div className="space-y-3">
        {jobs.map(job => (
          <PipelineJobCard
            key={job.job_id}
            job={job}
            onRefresh={fetchJobs}
            expanded={expandedJobId === job.job_id}
            onToggle={() => setExpandedJobId(
              expandedJobId === job.job_id ? null : job.job_id
            )}
          />
        ))}
      </div>
    </div>
  );
};

export default PipelineStatusTracker;
